var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { property, query, queryAll } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { styleMap } from 'lit/directives/style-map.js';
import { html, css } from 'lit';
import { numberDecoder, customElement, Clock, typeFormat, PICInput, booleanDecoder, PIC } from '../Internals';
let PICTime = class PICTime extends PICInput {
    get submitValue() {
        if (this.clock)
            return this.clock.getSec();
        return '';
    }
    constructor() {
        super();
        this.format = 0;
        this.pos = 0;
        this.lastInput = -1;
        this.showPicker = 'hidden';
        this.hasCaretMoved = false;
        this.min = 0;
        this.max = 86399;
        this.step = 1;
        this.unsafe_ = false;
    }
    get maxHoursCiphers() {
        if (this.max === Infinity)
            return null;
        return Math.floor(this.max / 3600).toString().length;
    }
    static get styles() {
        const defaultStyle = css `
            :host {
                --annoyingzindexrectifier: 5; //should always be 5, if we ever get rid of oldstyle 
            }
            .inner-input-old {
                width: 100%;
                box-sizing: border-box;
                font-family: monospace;
            }
            .picker {
                position: absolute;
                top: 40px;
                z-index: var(--annoyingzindexrectifier);
            }
            .picker.low {
                top: 67px;
            }
            .picker.medium {
                top: 59px;
            }
            .picker.high {
                top: 38px; 
            }
            .picker.inline {
                top: 28px;
            }
            .icon.inline {
                right: -72px;
            }
            `;
        return css `
            ${super.styles}
            ${defaultStyle}
        `;
    }
    connectedCallback() {
        super.connectedCallback();
        this.startUp();
    }
    getBlankValue() {
        let res;
        if (this.placeholder) {
            res = this.placeholder;
        }
        else {
            res = '––';
            for (let i = 1; i <= this.format; i++) {
                res += ':––';
            }
            ;
        }
        return res;
    }
    tearDown() {
        this.userHasInteracted = false;
        this.clock = null;
        this.value = this.getBlankValue();
    }
    startUp() {
        if (this.format == -1) {
            switch (PIC.getInstance.userConfig.timeFormat) {
                case typeFormat[typeFormat.HH]:
                    this.format = 0;
                    break;
                case typeFormat[typeFormat.HHMM]:
                    this.format = 1;
                    break;
                case typeFormat[typeFormat.HHMMSS]:
                    this.format = 2;
                    break;
            }
        }
        if (this.format == -1) {
            if (!this.value) {
                this.format = typeFormat.HHMM;
            }
            else {
                this.format = this.value.split(':').length - 1;
            }
        }
        if (!this.value || this.value === '?') {
            this.value = this.getBlankValue();
            this.clock = null;
        }
        else {
            this.clock = new Clock(parseInt(this.value), this.format, this.min, this.max, this.step);
            this.clock.forceValid();
            this.value = this.clock.toString();
        }
    }
    handleUserInteraction() {
        if (!this.userHasInteracted) {
            this.userHasInteracted = true;
            if (!this.clock) {
                this.tearDown();
                this.startUp();
            }
        }
    }
    // Save caret position before re-render
    willUpdate() {
        if (this.inputField)
            this.pos = this.getPos();
    }
    //Select saved caret position
    updated() {
        this.setPos(this.pos);
    }
    /* handle movement of selection */
    moveCaret(direction) {
        this.lastInput = this.getPos();
        // Check if the entire input field is selected
        if (this.inputField.selectionStart === 0 && this.inputField.selectionEnd === this.inputField.value.length) {
            if (direction === 'ArrowLeft' || direction === 'ArrowUp' || direction === 'ArrowDown') {
                this.setPos(typeFormat.HH);
                this.hasCaretMoved = true;
            }
            else if (direction === 'ArrowRight') {
                this.setPos(typeFormat.HHMM);
                this.hasCaretMoved = true;
            }
            return;
        }
        /* HHField only has one position */
        if (this.format == typeFormat.HH) {
            this.setPos(0);
            return;
        }
        let pos = this.getPos();
        if (direction == 'ArrowRight') {
            if (pos == this.format) {
                /* dont change pos if at edge */
                return;
            }
            else {
                this.setPos(pos + 1);
            }
        }
        else {
            if (pos == 0) {
                /* dont change pos if at edge */
                this.lastInput = -1;
                return;
            }
            else {
                this.setPos(pos - 1);
            }
        }
    }
    /* increment value at caret pos */
    increment() {
        var _a, _b;
        this.handleUserInteraction();
        this.value = (_b = (_a = this.clock) === null || _a === void 0 ? void 0 : _a.increment(this.getPos())) !== null && _b !== void 0 ? _b : this.getBlankValue();
    }
    /* decrement value at caret pos */
    decrement() {
        var _a, _b;
        this.handleUserInteraction();
        this.value = (_b = (_a = this.clock) === null || _a === void 0 ? void 0 : _a.decrement(this.getPos())) !== null && _b !== void 0 ? _b : this.getBlankValue();
    }
    /* pass number and pos to internal clock and update value */
    inputNumber(inNumber) {
        var _a;
        this.handleUserInteraction();
        let inputPosition = this.getPos(), lastInputSetter;
        if (inputPosition === typeFormat.ALL) {
            inputPosition = typeFormat.HH;
        }
        lastInputSetter = inputPosition;
        if (this.prevInputTimer)
            clearTimeout(this.prevInputTimer);
        this.prevInputTimer = setTimeout(() => this.prevInput = null, 2000); //this should REALLY not be like this... 
        if (this.clock.inputDigit(inNumber, inputPosition, this.lastInput)) {
            this.value = this.clock.toString();
            //(currently) for hours specifically (also 01, if zero has been pressed) - inputDigit startsOver with new values every 2sec (atm)
            //TODO: still an issue that tabbing out of the field does not reset the "input value"
            //on exit, internalSeconds % 3600 ?
            if ((this.getPos() === typeFormat.HH || this.getPos() === typeFormat.ALL)
                && this.max !== Infinity
                && (!this.value.startsWith('0') || this.prevInput === 0)
                && ((_a = this.value.split(':')[0]) === null || _a === void 0 ? void 0 : _a.length) === this.maxHoursCiphers) {
                this.setPos(typeFormat.HHMM);
            }
            else {
                this.setPos(this.value.length - 1);
            }
        }
        else {
            this.setPos(inputPosition);
            this.lastInput = lastInputSetter;
            this.value = this.clock.toString();
        }
        this.prevInput = inNumber;
    }
    getPos() {
        if (this.inputField.selectionStart == 0 && this.inputField.selectionEnd == this.inputField.value.length) {
            return typeFormat.ALL;
        }
        else if (this.inputField.selectionStart <= this.value.indexOf(':')) {
            return typeFormat.HH;
        }
        else if (this.inputField.selectionStart <= (this.value.length == (this.format * 2) + this.format ? this.value.lastIndexOf(':') : 5)) {
            return typeFormat.HHMM;
        }
        else {
            return typeFormat.HHMMSS;
        }
    }
    setPos(position) {
        switch (position) {
            case typeFormat.HH: {
                this.inputField.setSelectionRange(0, this.value.indexOf(':'));
                break;
            }
            case typeFormat.HHMM: {
                let minutesStart = this.value.indexOf(':') + 1;
                let minutesEnd = minutesStart + 2;
                this.inputField.setSelectionRange(minutesStart, minutesEnd);
                break;
            }
            case typeFormat.HHMMSS: {
                this.inputField.setSelectionRange((this.value.length == (this.format * 2) + this.format ? this.value.lastIndexOf(':') : 5) + 1, this.value.length);
                break;
            }
            case typeFormat.ALL: {
                this.inputField.setSelectionRange(0, this.value.length);
                break;
            }
        }
    }
    /* clear value at caret pos */
    clearPos() {
        var _a, _b, _c, _d, _e;
        switch (this.getPos()) {
            case typeFormat.HH: {
                (_a = this.clock) === null || _a === void 0 ? void 0 : _a.clearHour();
                break;
            }
            case typeFormat.HHMM: {
                (_b = this.clock) === null || _b === void 0 ? void 0 : _b.clearMinute();
                break;
            }
            case typeFormat.HHMMSS: {
                (_c = this.clock) === null || _c === void 0 ? void 0 : _c.clearMinute();
                break;
            }
            case typeFormat.ALL: {
                this.tearDown();
                /*this.clock?.clearHour();
                this.clock?.clearMinute();
                this.clock?.clearSecond();*/
                break;
            }
        }
        this.value = (_e = (_d = this.clock) === null || _d === void 0 ? void 0 : _d.toString()) !== null && _e !== void 0 ? _e : this.getBlankValue();
    }
    reset() {
        var _a, _b, _c;
        this.handleUserInteraction();
        //clock should always be instantiated here, handle differently? 
        (_a = this.clock) === null || _a === void 0 ? void 0 : _a.reset();
        this.value = (_c = (_b = this.clock) === null || _b === void 0 ? void 0 : _b.toString()) !== null && _c !== void 0 ? _c : this.getBlankValue();
    }
    resetCaretPosition() {
        this.setPos(typeFormat.HH);
        this.hasCaretMoved = false;
    }
    focus() {
        this.inputField.focus();
    }
    refresh(e) {
        if (this.clock) {
            this.clock.forceValid();
            this.value = this.clock.toString();
        }
        else {
            this.value = this.getBlankValue();
        }
        super.changeHandler(e);
    }
    addToClipBoard() {
        var _a, _b;
        const el = document.createElement('textarea');
        el.value = (_b = (_a = this.clock) === null || _a === void 0 ? void 0 : _a.toString()) !== null && _b !== void 0 ? _b : this.getBlankValue();
        document.body.appendChild(el);
        el.select();
        document.execCommand('copy');
        document.body.removeChild(el);
        this.inputField.select();
        this.setPos(this.getPos());
    }
    /* override keyEvents */
    handleKeyEvents(e) {
        if (!['Escape', 'Tab', 'Alt', 'Shift', 'F1', 'F2', 'F3', 'F4', 'F5', 'F6', 'F7', 'F8', 'F9', 'F10', 'F11', 'F12',].includes(e.key) && !(e.ctrlKey && e.key == 'v')) {
            e.preventDefault();
            e.stopImmediatePropagation();
        }
        if (['ArrowLeft', 'ArrowRight'].includes(e.key)) {
            this.moveCaret(e.key);
            this.hasCaretMoved = true;
        }
        else if (['ArrowUp', 'ArrowDown'].includes(e.key)) {
            if (!this.hasCaretMoved) {
                this.setPos(typeFormat.HH);
                this.hasCaretMoved = true;
            }
            else {
                if (this.getPos() === typeFormat.HH) {
                    if (e.key === 'ArrowUp') {
                        this.increment();
                    }
                    else if (e.key === 'ArrowDown') {
                        this.decrement();
                    }
                }
                else {
                    // Move caret to minutes if not on hours
                    this.moveCaret('ArrowRight');
                    if (e.key === 'ArrowUp') {
                        this.increment();
                    }
                    else if (e.key === 'ArrowDown') {
                        this.decrement();
                    }
                }
            }
        }
        else if (['Delete', 'Backspace'].includes(e.key)) {
            this.clearPos();
        }
        else if (['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'].includes(e.key)) {
            this.inputNumber(parseInt(e.key));
        }
        else if (['.'].includes(e.key)) {
            this.reset();
        }
        else if (e.ctrlKey && e.key == 'c') {
            this.addToClipBoard();
        }
        else if (['Enter'].includes(e.key)) {
            this.enterSubmit(e);
        }
        else {
            return false;
        }
    }
    handleTextEvent(e) {
        if (['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'].includes(e.data)) {
            this.handleUserInteraction();
            this.inputNumber(parseInt(e.data));
            this.requestUpdate();
        }
        else {
            if (this.clock) {
                this.clock.forceValid();
                this.value = this.clock.toString();
            }
            else {
                this.value = this.getBlankValue();
            }
            this.requestUpdate();
        }
    }
    handlePaste(e) {
        var _a, _b;
        e.preventDefault();
        e.stopImmediatePropagation();
        if (e.type == 'paste') {
            if (e.clipboardData) {
                let paste = e.clipboardData.getData('text').split(':');
                if (paste.length > 0 && !isNaN(Number(paste[0])) && !this.clock) {
                    this.handleUserInteraction();
                }
                for (let i = 0; i < paste.length; i++) {
                    let val = Number(paste[i]);
                    if (!isNaN(val)) {
                        switch (i) {
                            case typeFormat.HH: {
                                if (val < 24)
                                    this.clock.setHour(val);
                                break;
                            }
                            case typeFormat.HHMM: {
                                if (val < 60)
                                    this.clock.setMinute(val);
                                break;
                            }
                            case typeFormat.HHMMSS: {
                                if (val < 60)
                                    this.clock.setSecond(val);
                                break;
                            }
                        }
                    }
                }
                this.value = (_b = (_a = this.clock) === null || _a === void 0 ? void 0 : _a.toString()) !== null && _b !== void 0 ? _b : this.getBlankValue();
            }
        }
    }
    handleClick(e) {
        if (e.target) {
            this.setPos(this.getPos());
            this.lastInput = -1;
        }
    }
    handleDblClick(e) {
        if (e.target) {
            this.setPos(typeFormat.ALL);
            this.lastInput = -1;
        }
    }
    iconClick() {
        var _a, _b, _c, _d;
        if (this.eType == 'old') {
            if (this.showPicker == 'show') {
                this.showPicker = 'fade';
                /*document.removeEventListener('click', this.closeListener as EventListener);*/
            }
            else if (this.showPicker == 'hidden') {
                this.showPicker = 'show';
            }
            if (this.showPicker == 'show')
                setTimeout(() => {
                    let elems = this.selectedCollection;
                    for (let i = 0; i < elems.length; i++) {
                        let elem = elems[i];
                        let top = elem.offsetTop;
                        elem.parentElement.scrollTop = top;
                    }
                }, 1);
            this.requestUpdate();
        }
        else {
            this.timePicker_.shouldHide = false;
            this.timePicker_.data = {
                parent: this,
                hours: parseInt((_b = (_a = this.clock) === null || _a === void 0 ? void 0 : _a.getHour()) !== null && _b !== void 0 ? _b : '0'),
                minutes: parseInt((_d = (_c = this.clock) === null || _c === void 0 ? void 0 : _c.getMinute()) !== null && _d !== void 0 ? _d : '0')
            };
            this.timePicker_.toggle();
        }
    }
    toggle() {
        if (this.showPicker == "show") {
            this.showPicker = "fade";
        }
        else if (this.showPicker == 'hidden') {
            this.showPicker = 'show';
        }
        this.requestUpdate();
    }
    forceClose() {
        if (this.eType == 'old') {
            if (this.showPicker == "show") {
                this.showPicker = "fade";
                this.requestUpdate();
            }
        }
        else {
            if (this.timePicker_)
                this.timePicker_.hide();
        }
    }
    renderPicker(iShow) {
        if (!this.closeListener) {
            let ref = this;
            this.closeListener = (e) => {
                let clickInside = false;
                if (e.target.renderRoot) {
                    if (e.target.renderRoot.querySelector('#picker') == ref) {
                        clickInside = true;
                    }
                    else if (e.target == ref) {
                        clickInside = true;
                    }
                    else if (e.composedPath()[3]) {
                        if (e.composedPath()[3] == ref || e.composedPath()[5] == ref) {
                            clickInside = true;
                        }
                        ;
                    }
                    else {
                        clickInside = e.composedPath().includes(ref);
                    }
                }
                if (!clickInside && ref.showPicker == 'show')
                    ref.toggle();
            };
            document.addEventListener('click', this.closeListener);
        }
        let classes = {
            picker: true,
            show: iShow == 'show',
            hidden: iShow == 'hidden',
            fade: iShow == 'fade'
        };
        return html `
                <style>
                    .picker {
                        top: -14px;
                        min-height: 25px;
                        max-height: 100px;
                        width: 8rem;
                        background: rgb(167 167 167);
                        position: relative;
                        -moz-border-radius: 10px;
                        -webkit-border-radius: 10px;
                        border-radius: 10px;
                        overflow: hidden;
                        display: flex;
                        box-shadow: rgba(0, 0, 0, 0.3) 0px 19px 38px, rgba(0, 0, 0, 0.22) 0px 15px 12px;
                        border: 1px solid black;
                    }
                    .picker.hidden {
                        display: none;
                    }
                    .picker.fade {
                        animation: fadeOut ease 0.3s;
                        -webkit-animation: fadeOut ease 0.3s;
                        -moz-animation: fadeOut ease 0.3s;
                    }
                    .picker.show {
                        display: flex;
                        animation: fadeIn ease 0.3s;
                        -webkit-animation: fadeIn ease 0.3s;
                        -moz-animation: fadeIn ease 0.3s;
                    }
                    .pick-child {
                        flex: 1;
                        overflow-y: scroll;
                        scrollbar-width: none;
                    }
                    ::-webkit-scrollbar {
                        width: 0px;
                        background: transparent; /* make scrollbar transparent */
                    }
                    .item {
                        display: flex;
                        justify-content: center;
                        cursor: pointer;
                    }
                    .item.selected {
                        background-color: white;
                    }
                    @keyframes fadeIn {
                        0% {opacity: 0;}
                        100% {opacity: 1;}
                    }
                    @-moz-keyframes fadeIn {
                        0% {opacity: 0;}
                        100% {opacity: 1;}
                    }
                    @-webkit-keyframes fadeIn {
                        0% {opacity: 0;}
                        100% {opacity: 1;}
                    }
                    @keyframes fadeOut {
                        0% {opacity: 1;}
                        100% {opacity: 0;}
                    }
                    @-moz-keyframes fadeOut {
                        0% {opacity: 1;}
                        100% {opacity: 0;}
                    }
                    @-webkit-keyframes fadeOut {
                        0% {opacity: 1;}
                        100% {opacity: 0;}
                    }
                </style>
                ${this.clock ? html `
                    <div class=${classMap(classes)} @animationend=${this.handleend}>
                        ${this.renderPickChild(this.clock.getPosValid()[typeFormat.HH], this.clock.getHour(), this.clickHour, this)}
                        ${this.format >= typeFormat.HHMM ? this.renderPickChild(this.clock.getPosValid()[typeFormat.HHMM], this.clock.getMinute(), this.clickMinute, this) : ''}
                        ${this.format >= typeFormat.HHMMSS ? this.renderPickChild(this.clock.getPosValid()[typeFormat.HHMMSS], this.clock.getSecond(), this.clickSecond, this) : ''}
                    </div>`
            : html `
                    <div class=${classMap(classes)} @animationend=${this.handleend}>
                        ${this.renderPickChild([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24], '00', this.clickHour, this)}
                        ${this.format >= typeFormat.HHMM ? this.renderPickChild([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 27, 28, 29, 30, 31, 32, 33, 34, 35, 36, 37, 38, 39, 40, 41, 42, 43, 44, 45, 46, 47, 48, 49, 50, 51, 52, 53, 54, 55, 56, 57, 58, 59], '00', this.clickMinute, this) : ''}
                        ${this.format >= typeFormat.HHMMSS ? this.renderPickChild([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 27, 28, 29, 30, 31, 32, 33, 34, 35, 36, 37, 38, 39, 40, 41, 42, 43, 44, 45, 46, 47, 48, 49, 50, 51, 52, 53, 54, 55, 56, 57, 58, 59], '00', this.clickSecond, this) : ''}
                    </div>
                `}
            `;
    }
    renderPickChild(m, compVal, clickFunc, ref) {
        if (!m)
            return;
        return html `<div class="pick-child">
            ${m.map((mi) => {
            return html `
                <div 
                class="item ${compVal == (mi + '').padStart(2, '0') ? 'selected' : ''}"
                @click=${() => { clickFunc((mi + '').padStart(2, '0'), ref); }}
                >
                ${(mi + '').padStart(2, '0')}
            </div>
            `;
        })}
        </div>`;
    }
    handleend(e) {
        if (e.animationName == 'fadeOut') {
            this.showPicker = 'hidden';
            this.requestUpdate();
        }
    }
    clickHour(iHour, ref) {
        ref.handleUserInteraction();
        ref.clock.setHour(parseInt(iHour));
        ref.requestUpdate();
    }
    clickMinute(iMinute, ref) {
        ref.handleUserInteraction();
        ref.clock.setMinute(parseInt(iMinute));
        ref.requestUpdate();
    }
    clickSecond(iSecond, ref) {
        ref.handleUserInteraction();
        ref.clock.setSecond(parseInt(iSecond));
        ref.requestUpdate();
    }
    firstUpdated() {
        this.initValue_ = this.submitValue;
    }
    handleCMDUpdate(e) {
        let cmd = e.detail;
        if (e.detail.data)
            cmd = e.detail.data;
        if (typeof cmd === 'string') {
            let hours = 0;
            let mins = 0;
            let secs = 0;
            if (cmd.includes(':')) { // if format is HH:MM:SS or HH:MM
                let timeUnits = cmd.split(':');
                if (timeUnits[0] && !isNaN(Number(timeUnits[0])))
                    hours = Number(timeUnits[0]);
                if (timeUnits[1] && !isNaN(Number(timeUnits[1])))
                    mins = Number(timeUnits[1]);
                if (timeUnits[2] && !isNaN(Number(timeUnits[2])))
                    secs = Number(timeUnits[2]);
            }
            else if (!isNaN(Number(cmd))) { // if format is number of seconds since midnight
                let timeStr = Number(cmd);
                hours = parseInt(String(timeStr / 3600));
                mins = parseInt(String(timeStr % 3600 / 60));
                secs = timeStr % 3600 % 60;
            }
            if (!this.clock) {
                this.setValue(cmd);
                this.startUp();
            }
            else {
                this.clock.setHour(hours);
                if (this.format > 0)
                    this.clock.setMinute(mins);
                if (this.format > 1)
                    this.clock.setSecond(secs);
                this.value = this.clock.toString();
            }
            this.requestUpdate();
        }
    }
    renderNewPicker() {
        return html `
            <pic-timepicker2 @timeselected=${(e) => { handleTimeSelected(e, this); }} id="timepicker"></pic-timepicker2>
        `;
        function handleTimeSelected(iEvent, timeRef) {
            var _a, _b, _c;
            timeRef.handleUserInteraction();
            if (iEvent.detail.type == 'select') {
                (_a = timeRef.clock) === null || _a === void 0 ? void 0 : _a.setHour(iEvent.detail.hours);
                (_b = timeRef.clock) === null || _b === void 0 ? void 0 : _b.setMinute(iEvent.detail.minutes);
                timeRef.refresh( /* NEEDS USEREVENT REF */);
            }
            if (iEvent.detail.type === 'cancel' || iEvent.detail.type === 'select') {
                let inModal = ((_c = timeRef.getRootNode().firstElementChild) === null || _c === void 0 ? void 0 : _c.nodeName) === 'PIC-MODAL';
                if (inModal)
                    timeRef.focus();
            }
        }
    }
    render() {
        var _a, _b;
        this.value = (_b = (_a = this.clock) === null || _a === void 0 ? void 0 : _a.toString()) !== null && _b !== void 0 ? _b : this.getBlankValue();
        this.setComponentInlineStyle();
        return html `
        ${this.renderCSS(true)}
        <div style=${styleMap(this.getContainerStyle())} class=${classMap(this.getContainerClassMap())}>
            <label class=${classMap(this.getInputLabelClass())} for=${this.id_ + '_inner'}>${this.label}</label>
            <input
            id=${this.id_ + '_inner'}
            name=${this.id} 
            type="text"
            class=${classMap(this.getInnerInputClass())}
            .value=${this.value}
            ?disabled=${this.disabled_}
            placeholder=${this.placeholder}
            @keydown=${this.handleKeyEvents}
            @click=${this.handleClick}
            @dblclick=${this.handleDblClick}
            @paste=${this.handlePaste}
            @blur=${this.resetCaretPosition}
            @focusout=${this.refresh}
            @textInput=${this.handleTextEvent}
            />
            ${this.icon.length > 0 ? this.renderIcon(this.disabled_) : ''}
            ${this.eType == 'old' ? this.renderPicker(this.showPicker) : this.renderNewPicker()}
            ${this.renderErr(this.showFullError)}
        </div>
        ${this.tooltip_ ? html `<pic-tooltip>${this.tooltip_}</pic-tooltip>` : ''}
        `;
    }
};
__decorate([
    query('.inner-input')
], PICTime.prototype, "inputField", void 0);
__decorate([
    queryAll('.item.selected')
], PICTime.prototype, "selectedCollection", void 0);
__decorate([
    property({ converter: numberDecoder })
], PICTime.prototype, "format", void 0);
__decorate([
    property({ converter: numberDecoder })
], PICTime.prototype, "min", void 0);
__decorate([
    property({ converter: numberDecoder })
], PICTime.prototype, "max", void 0);
__decorate([
    property({ converter: numberDecoder })
], PICTime.prototype, "step", void 0);
__decorate([
    property({ converter: booleanDecoder, attribute: 'unsafe' })
], PICTime.prototype, "unsafe_", void 0);
__decorate([
    query('#timepicker')
], PICTime.prototype, "timePicker_", void 0);
PICTime = __decorate([
    customElement('pic-time')
], PICTime);
export { PICTime };
//# sourceMappingURL=PICTime.js.map